/**
 * Catálogo BUILT-IN de módulos de exames + tipos de atendimento.
 *
 * Tudo aqui é estático e versionado no código (não vem do Firestore). A próxima
 * iteração vai permitir a clínica criar tipos próprios via Configurações — esses
 * serão sobrepostos a esta lista, não vão substituí-la.
 *
 * Princípios:
 *   - Slugs estáveis (mudar quebra dados salvos)
 *   - Cobertura ampla mas não exaustiva — começamos com os mais usados em
 *     oftalmologia geral; especialidades (retina, plástica) podem ser
 *     adicionadas depois.
 *   - Campos seguem o padrão OD/OE quando faz sentido clínico.
 */

import type { EncounterTypeDef, ExamModuleDef } from '@/types/encounter';

// ─── Presets de texto reutilizados (ver ExamFieldDef.presets) ───

/** Escala de AV pra longe (Snellen) + notações de baixa visão. */
const AV_LONGE_PRESETS = [
  '20/20', '20/25', '20/30', '20/40', '20/50', '20/60', '20/70', '20/80',
  '20/100', '20/200', '20/400', 'CD', 'MM', 'PL', 'SPL',
];

/** Escala de AV pra perto (Jaeger). */
const AV_PERTO_PRESETS = ['J1', 'J2', 'J3', 'J4', 'J5', 'J6'];

// ─── Módulos de exame ───

export const BUILTIN_EXAM_MODULES: ExamModuleDef[] = [
  // — Anamnese — (M2 "fonte única": estes módulos têm seção própria na
  // evolução gerada — ver src/utils/composeEvolucao.ts. Hipótese/CID já
  // existiam na categoria diagnóstico e são reaproveitados lá.)
  {
    id: 'queixa-principal',
    label: 'Queixa principal',
    hint: 'QP',
    category: 'anamnese',
    perVisit: true,
    fields: [
      {
        key: 'queixa',
        label: 'Queixa',
        layout: 'mono',
        kind: 'textarea',
        placeholder: 'Ex.: baixa acuidade visual progressiva em OD há 6 meses',
        presets: [
          'Consulta de rotina, sem queixas',
          'Renovação de óculos',
          'Baixa acuidade visual para longe',
          'Dificuldade para leitura / perto',
          'Baixa acuidade visual progressiva',
          'Ardência e sensação de areia nos olhos',
          'Olho vermelho',
          'Prurido ocular',
          'Lacrimejamento',
          'Dor ocular',
          'Cefaleia ao esforço visual',
          'Moscas volantes',
          'Flashes luminosos (fotopsias)',
          'Visão dupla (diplopia)',
          'Retorno com exames',
          'Acompanhamento de glaucoma',
          'Avaliação para cirurgia de catarata',
        ],
      },
    ],
  },
  // HDA (07/10/2026): a narrativa da queixa ganhou módulo próprio — antes só
  // existia dentro do texto do Prontuário e o ditado saía resumido demais.
  {
    id: 'historia-doenca-atual',
    label: 'História da doença atual',
    hint: 'HDA',
    category: 'anamnese',
    perVisit: true,
    fields: [
      {
        key: 'historia',
        label: 'História',
        layout: 'mono',
        kind: 'textarea',
        placeholder:
          'Ex.: refere embaçamento visual progressivo em OD há cerca de 6 meses, pior para longe e à noite, com ofuscamento por faróis; nega dor, olho vermelho ou flashes…',
      },
    ],
  },
  // (M4) Antecedentes — fatos do paciente: preenche uma vez e o carry-over
  // traz de volta na próxima consulta (sem a flag perVisit).
  {
    id: 'antecedentes-pessoais',
    label: 'Antecedentes pessoais',
    hint: 'AP',
    category: 'anamnese',
    fields: [
      {
        key: 'antecedentes',
        label: 'Antecedentes',
        layout: 'mono',
        kind: 'textarea',
        placeholder: 'Ex.: HAS, DM2 em uso de metformina; facectomia OE 2020; alergia a dipirona',
      },
    ],
  },
  {
    id: 'antecedentes-familiares',
    label: 'Antecedentes familiares',
    hint: 'AF',
    category: 'anamnese',
    fields: [
      {
        key: 'antecedentes',
        label: 'Antecedentes',
        layout: 'mono',
        kind: 'textarea',
        placeholder: 'Ex.: glaucoma (mãe), DMRI (avô paterno)',
      },
    ],
  },
  // — Refração —
  {
    id: 'lensometria',
    label: 'Lensometria',
    hint: 'Óculos em uso',
    category: 'refracao',
    fields: [
      { key: 'esferico', label: 'Esférico', suffix: 'D' },
      { key: 'cilindrico', label: 'Cilíndrico', suffix: 'D' },
      { key: 'eixo', label: 'Eixo', suffix: '°' },
      // A adição costuma ser a MESMA nos dois olhos → aceita o espelho "AO".
      { key: 'adicao', label: 'Adição', suffix: 'D', mirrorable: true },
      { key: 'prisma', label: 'Prisma', suffix: 'Δ' },
    ],
  },
  // Módulo unificado (M3 — enxugamento): substitui os 4 módulos de refração
  // (objetiva/subjetiva × com/sem cicloplegia). Objetiva e subjetiva como
  // linhas separadas; cicloplegia como marcador. Os antigos ficam abaixo,
  // `deprecated` (só pra renderizar dados já salvos).
  {
    id: 'refracao',
    label: 'Refração',
    hint: 'Objetiva + subjetiva',
    category: 'refracao',
    // Matriz agrupada: OD/OE em linhas; Objetiva e Subjetiva como grupos de
    // colunas esf/cil/eixo(/adição). A cicloplegia (select) fica abaixo.
    matrixGroups: [
      { label: 'Objetiva', keys: ['obj_esferico', 'obj_cilindrico', 'obj_eixo'] },
      { label: 'Subjetiva', keys: ['subj_esferico', 'subj_cilindrico', 'subj_eixo', 'subj_adicao'] },
    ],
    fields: [
      { key: 'obj_esferico', label: 'Obj. esférico', shortLabel: 'Esf.', suffix: 'D' },
      { key: 'obj_cilindrico', label: 'Obj. cilíndrico', shortLabel: 'Cil.', suffix: 'D' },
      { key: 'obj_eixo', label: 'Obj. eixo', shortLabel: 'Eixo', suffix: '°' },
      { key: 'subj_esferico', label: 'Subj. esférico', shortLabel: 'Esf.', suffix: 'D' },
      { key: 'subj_cilindrico', label: 'Subj. cilíndrico', shortLabel: 'Cil.', suffix: 'D' },
      { key: 'subj_eixo', label: 'Subj. eixo', shortLabel: 'Eixo', suffix: '°' },
      // A adição costuma ser a MESMA nos dois olhos → aceita o espelho "AO".
      { key: 'subj_adicao', label: 'Subj. adição', shortLabel: 'Ad.', suffix: 'D', mirrorable: true },
      {
        key: 'cicloplegia',
        label: 'Cicloplegia',
        layout: 'mono',
        kind: 'select',
        options: ['Sem cicloplegia', 'Sob cicloplegia'],
      },
    ],
  },
  {
    id: 'refracao-obj-dinamica',
    label: 'Refração Objetiva Dinâmica',
    hint: 'Sem cicloplegia',
    category: 'refracao',
    deprecated: true,
    fields: [
      { key: 'esferico', label: 'Esférico', suffix: 'D' },
      { key: 'cilindrico', label: 'Cilíndrico', suffix: 'D' },
      { key: 'eixo', label: 'Eixo', suffix: '°' },
    ],
  },
  {
    id: 'refracao-obj-estatica',
    label: 'Refração Objetiva Estática',
    hint: 'Sob cicloplegia',
    category: 'refracao',
    deprecated: true,
    fields: [
      { key: 'esferico', label: 'Esférico', suffix: 'D' },
      { key: 'cilindrico', label: 'Cilíndrico', suffix: 'D' },
      { key: 'eixo', label: 'Eixo', suffix: '°' },
    ],
  },
  {
    id: 'refracao-subj-dinamica',
    label: 'Refração Subjetiva Dinâmica',
    hint: 'Sem cicloplegia',
    category: 'refracao',
    deprecated: true,
    fields: [
      { key: 'esferico', label: 'Esférico', suffix: 'D' },
      { key: 'cilindrico', label: 'Cilíndrico', suffix: 'D' },
      { key: 'eixo', label: 'Eixo', suffix: '°' },
      { key: 'adicao', label: 'Adição', suffix: 'D', mirrorable: true },
    ],
  },
  {
    id: 'refracao-subj-estatica',
    label: 'Refração Subjetiva Estática',
    hint: 'Sob cicloplegia',
    category: 'refracao',
    deprecated: true,
    fields: [
      { key: 'esferico', label: 'Esférico', suffix: 'D' },
      { key: 'cilindrico', label: 'Cilíndrico', suffix: 'D' },
      { key: 'eixo', label: 'Eixo', suffix: '°' },
      { key: 'adicao', label: 'Adição', suffix: 'D', mirrorable: true },
    ],
  },

  // — Acuidade visual —
  // Módulo unificado (M3): substitui os 6 módulos de AV (longe/perto ×
  // com/sem correção × pós-refração) por um só com 1 campo por condição.
  // Os antigos ficam abaixo, `deprecated` (só renderizam dados já salvos).
  {
    id: 'acuidade-visual',
    label: 'Acuidade visual',
    hint: 'Longe/perto · com/sem correção · pós-refração',
    category: 'acuidade',
    // Matriz agrupada: OD/OE em linhas; Longe e Perto como grupos de colunas
    // s/c · c/c · pós-refração.
    matrixGroups: [
      { label: 'Longe', keys: ['avlsc', 'avlcc', 'av_longe_pos'] },
      { label: 'Perto', keys: ['avpsc', 'avpcc', 'av_perto_pos'] },
    ],
    fields: [
      { key: 'avlsc', label: 'Longe s/ correção', shortLabel: 's/c', placeholder: '20/20', presets: AV_LONGE_PRESETS },
      { key: 'avlcc', label: 'Longe c/ correção', shortLabel: 'c/c', placeholder: '20/20', presets: AV_LONGE_PRESETS },
      { key: 'avpsc', label: 'Perto s/ correção', shortLabel: 's/c', placeholder: 'J1', presets: AV_PERTO_PRESETS },
      { key: 'avpcc', label: 'Perto c/ correção', shortLabel: 'c/c', placeholder: 'J1', presets: AV_PERTO_PRESETS },
      { key: 'av_longe_pos', label: 'Longe pós-refração', shortLabel: 'pós', placeholder: '20/20', presets: AV_LONGE_PRESETS },
      { key: 'av_perto_pos', label: 'Perto pós-refração', shortLabel: 'pós', placeholder: 'J1', presets: AV_PERTO_PRESETS },
    ],
  },
  {
    id: 'avlsc',
    label: 'AVLSC',
    hint: 'AV para longe sem correção',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: '20/20', presets: AV_LONGE_PRESETS }],
  },
  {
    id: 'avlcc',
    label: 'AVLCC',
    hint: 'AV para longe com correção',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: '20/20', presets: AV_LONGE_PRESETS }],
  },
  {
    id: 'avpsc',
    label: 'AVPSC',
    hint: 'AV para perto sem correção',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: 'J1', presets: AV_PERTO_PRESETS }],
  },
  {
    id: 'avpcc',
    label: 'AVPCC',
    hint: 'AV para perto com correção',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: 'J1', presets: AV_PERTO_PRESETS }],
  },
  {
    id: 'av-longe-pos-refracao',
    label: 'AV para longe após refração',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: '20/20', presets: AV_LONGE_PRESETS }],
  },
  {
    id: 'av-perto-pos-refracao',
    label: 'AV para perto após refração',
    category: 'acuidade',
    deprecated: true,
    fields: [{ key: 'av', label: 'AV', placeholder: 'J1', presets: AV_PERTO_PRESETS }],
  },

  // — Pressão —
  {
    id: 'pio',
    label: 'Pressão intraocular',
    shortLabel: 'PIO',
    hint: 'PIO — Tonometria de aplanação',
    category: 'pressao',
    fields: [{ key: 'pio', label: 'PIO', kind: 'number', suffix: 'mmHg', placeholder: '14' }],
  },
  // (M5) Tonometria de não-contato — aparelho de sopro. Módulo separado do
  // `pio` (aplanação) porque os valores NÃO são intercambiáveis clinicamente.
  {
    id: 'pio-sopro',
    label: 'PIO — sopro',
    shortLabel: 'PIO sopro',
    hint: 'Tonometria de não-contato',
    category: 'pressao',
    fields: [{ key: 'pio', label: 'PIO', kind: 'number', suffix: 'mmHg', placeholder: '15' }],
  },
  // Tonometria basal (28/10/2026): a PIO mais antiga que o médico registrou
  // — consultas do dratento (módulo `pio`) e exames "Tonometria" do HiDoctor.
  // Preenchida sozinha ao abrir o módulo, com a data do registro ao lado
  // (utils/historyExams.ts). Pro relatório de glaucoma: {{ex:pio-basal}}.
  {
    id: 'pio-basal',
    label: 'Tonometria basal',
    shortLabel: 'PIO basal',
    hint: 'PIO mais antiga registrada por você — automática',
    category: 'pressao',
    fromHistory: true,
    fields: [
      { key: 'pio', label: 'PIO', kind: 'number', suffix: 'mmHg' },
      { key: 'data', label: 'Data', layout: 'mono', placeholder: 'DD/MM/AAAA' },
    ],
  },
  {
    id: 'paquimetria',
    label: 'Paquimetria',
    shortLabel: 'Paqui.',
    hint: 'Espessura corneana',
    category: 'pressao',
    fields: [{ key: 'espessura', label: 'Espessura', kind: 'number', suffix: 'µm', placeholder: '545' }],
  },

  // — Segmento anterior —
  {
    id: 'biomicroscopia',
    label: 'Biomicroscopia',
    hint: 'Segmento anterior',
    category: 'segmento_anterior',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      placeholder: 'Córnea, câmara, íris, cristalino…',
      presets: [
        'Sem alterações',
        'Córnea transparente, câmara ampla e formada, íris trófica, cristalino transparente',
        'Catarata nuclear +/4+',
        'Catarata nuclear ++/4+',
        'Catarata córtico-nuclear ++/4+',
        'Catarata subcapsular posterior',
        'Pseudofacia — LIO tópica e centrada',
        'Blefarite / disfunção de glândulas de Meibômio',
        'Ceratite ponteada superficial',
        'Hiperemia conjuntival',
        'Pterígio nasal',
        'Opacidade de cápsula posterior',
      ],
    }],
  },
  {
    id: 'ceratometria',
    label: 'Ceratometria',
    hint: 'K1/K2',
    category: 'segmento_anterior',
    fields: [
      { key: 'k1', label: 'K1', suffix: 'D' },
      { key: 'k2', label: 'K2', suffix: 'D' },
      { key: 'eixo', label: 'Eixo K1', suffix: '°' },
    ],
  },

  // Microscopia especular do endotélio — usada também pelo card de "valores
  // do exame" dos registros de imagem (dados lidos do printout do aparelho).
  {
    id: 'microscopia-especular',
    label: 'Microscopia especular',
    hint: 'Endotélio corneano',
    category: 'segmento_anterior',
    fields: [
      { key: 'cd', label: 'CD', kind: 'number', suffix: 'cél/mm²', placeholder: '2400' },
      { key: 'cct', label: 'CCT', kind: 'number', suffix: 'µm', placeholder: '545' },
      { key: 'avg', label: 'AVG', kind: 'number', suffix: 'µm²' },
      { key: 'sd', label: 'SD', kind: 'number', suffix: 'µm²' },
      { key: 'cv', label: 'CV', kind: 'number', suffix: '%' },
      { key: 'hex', label: 'Hex (6A)', kind: 'number', suffix: '%' },
      { key: 'num', label: 'Nº células', kind: 'number' },
    ],
  },

  // — Segmento posterior —
  {
    id: 'fundoscopia',
    label: 'Fundoscopia',
    hint: 'Fundo de olho',
    category: 'segmento_posterior',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      placeholder: 'Disco, mácula, vasos, periferia…',
      presets: [
        'Sem alterações',
        'Disco corado, contornos nítidos, escavação fisiológica; mácula e vasos sem alterações; retina aplicada',
        'Escavação aumentada',
        'Palidez de disco óptico',
        'Retinopatia diabética não proliferativa leve',
        'Retinopatia diabética não proliferativa moderada',
        'Retinopatia diabética proliferativa',
        'Drusas maculares',
        'Alterações do EPR na mácula',
        'Membrana epirretiniana',
        'Meio opaco — fundoscopia prejudicada',
      ],
    }],
  },
  // Mapeamento de retina (pedido de 26/10/2026): oftalmoscopia indireta sob
  // midríase — vítreo e retina periférica. Separado da fundoscopia (polo
  // posterior) porque é exame próprio, com laudo e cobrança próprios.
  {
    id: 'mapeamento-retina',
    label: 'Mapeamento de retina',
    hint: 'Oftalmoscopia indireta',
    category: 'segmento_posterior',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      placeholder: 'Vítreo, retina periférica, roturas, degenerações…',
      presets: [
        'Sem alterações',
        'Vítreo transparente, retina aplicada em 360°, sem roturas, sem degenerações periféricas',
        'Exame sob midríase, com depressão escleral',
        'Descolamento do vítreo posterior (anel de Weiss)',
        'Degeneração lattice (em paliçada) sem roturas',
        'Degeneração lattice com buracos atróficos',
        'Degeneração pavimentosa',
        'Branco sem pressão',
        'Buraco retiniano atrófico',
        'Rotura retiniana em ferradura',
        'Retinosquise periférica',
        'Tração vitreorretiniana',
        'Descolamento de retina regmatogênico',
        'Cicatrizes de fotocoagulação a laser',
        'Hemorragias retinianas periféricas',
        'Meio opaco — mapeamento prejudicado',
      ],
    }],
  },
  {
    id: 'relacao-escavacao-disco',
    label: 'Escavação/Disco',
    hint: 'Relação E/D',
    category: 'segmento_posterior',
    fields: [{ key: 'razao', label: 'E/D', placeholder: '0.3', presets: ['0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'] }],
  },

  // — Biometria (pré-op catarata) —
  {
    id: 'biometria',
    label: 'Biometria',
    hint: 'IOLMaster / aplicação',
    category: 'biometria',
    fields: [
      { key: 'al', label: 'AL', kind: 'number', suffix: 'mm', placeholder: '23.50' },
      { key: 'acd', label: 'ACD', kind: 'number', suffix: 'mm' },
      { key: 'lt', label: 'LT', kind: 'number', suffix: 'mm' },
      { key: 'wtw', label: 'WTW', kind: 'number', suffix: 'mm', placeholder: '11.8' },
      { key: 'cct', label: 'CCT', kind: 'number', suffix: 'µm', placeholder: '545' },
      { key: 'k1', label: 'K1', suffix: 'D' },
      { key: 'k2', label: 'K2', suffix: 'D' },
      { key: 'lio_alvo', label: 'LIO alvo', suffix: 'D' },
      { key: 'lio_modelo', label: 'Modelo LIO', layout: 'mono' },
    ],
  },
  {
    id: 'topografia',
    label: 'Topografia / Tomografia',
    hint: 'Pentacam / Galilei',
    category: 'biometria',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      layout: 'mono',
      placeholder: 'Padrão, BFS, ELE/PLE, paquimetria mínima…',
      presets: [
        'Padrão regular e simétrico, sem sinais de ectasia',
        'Astigmatismo regular simétrico',
        'Padrão suspeito — assimetria inferior-superior',
        'Ectasia corneana / ceratocone',
      ],
    }],
  },

  // — Outros —
  {
    id: 'motilidade',
    label: 'Motilidade ocular',
    category: 'outros',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      layout: 'mono',
      placeholder: 'Versões, ducções, cover test…',
      presets: [
        'Versões e ducções preservadas, sem restrições',
        'Ortotropia em todas as posições do olhar',
        'Esotropia',
        'Exotropia',
        'Limitação de abdução',
        'Nistagmo',
      ],
    }],
  },
  {
    id: 'cover-test',
    label: 'Cover test',
    hint: 'PFC / PFV — prismas',
    category: 'outros',
    fields: [
      { key: 'pfc', label: 'PFC', layout: 'mono', placeholder: 'Ortoforia / ET / XT / HT…', presets: ['Ortoforia', 'ET', 'XT', 'HT', 'E(T)', 'X(T)'] },
      { key: 'pfv', label: 'PFV', layout: 'mono', placeholder: 'Ortoforia / ET / XT / HT…', presets: ['Ortoforia', 'ET', 'XT', 'HT', 'E(T)', 'X(T)'] },
      { key: 'prisma', label: 'Prisma (medida)', suffix: 'Δ', layout: 'mono' },
    ],
  },
  {
    id: 'campo-visual',
    label: 'Campo visual (confrontação)',
    category: 'outros',
    fields: [{
      key: 'achados',
      label: 'Achados',
      kind: 'textarea',
      placeholder: 'Normal / alteração…',
      presets: [
        'Normal à confrontação',
        'Defeito campimétrico temporal',
        'Defeito campimétrico nasal',
        'Defeito altitudinal',
        'Não colaborativo ao exame',
      ],
    }],
  },
  {
    id: 'schirmer',
    label: 'Teste de Schirmer',
    hint: 'Sem anestesia / 5 min',
    category: 'outros',
    fields: [{ key: 'mm', label: 'Schirmer', kind: 'number', suffix: 'mm', placeholder: '15' }],
  },
  {
    id: 'but',
    label: 'BUT (Break-up time)',
    hint: 'Tempo de ruptura do filme lacrimal',
    category: 'outros',
    fields: [{ key: 'segundos', label: 'BUT', kind: 'number', suffix: 's', placeholder: '10' }],
  },
  {
    id: 'gonioscopia',
    label: 'Gonioscopia',
    hint: 'Graduação Shaffer (0–4)',
    category: 'outros',
    fields: [
      {
        key: 'shaffer',
        label: 'Shaffer',
        placeholder: 'III–IV (aberto)',
        presets: ['IV (aberto)', 'III–IV (aberto)', 'III', 'II', 'I', '0 (fechado)'],
      },
      {
        key: 'achados',
        label: 'Achados',
        kind: 'textarea',
        placeholder: 'Pigmento, sinéquias, recesso…',
        presets: [
          'Ângulo aberto, sem sinéquias; pigmentação trabecular discreta',
          'Pigmentação trabecular acentuada',
          'Sinéquias anteriores periféricas',
          'Recesso angular',
        ],
      },
    ],
  },
  {
    id: 'pupilometria',
    label: 'Pupilometria',
    hint: 'mm — escotópica/mesópica/fotópica',
    category: 'outros',
    fields: [
      { key: 'escotopica', label: 'Escotópica', kind: 'number', suffix: 'mm', placeholder: '6.5' },
      { key: 'mesopica', label: 'Mesópica', kind: 'number', suffix: 'mm', placeholder: '4.5' },
      { key: 'fotopica', label: 'Fotópica', kind: 'number', suffix: 'mm', placeholder: '3.0' },
    ],
  },
  {
    id: 'hertel',
    label: 'Exoftalmometria (Hertel)',
    hint: 'Protrusão ocular',
    category: 'outros',
    fields: [
      { key: 'mm', label: 'Protrusão', kind: 'number', suffix: 'mm', placeholder: '16' },
      { key: 'base', label: 'Base', kind: 'number', suffix: 'mm', layout: 'mono', placeholder: '102' },
    ],
  },
  {
    id: 'visao-cores',
    label: 'Visão de cores (Ishihara)',
    category: 'outros',
    fields: [
      { key: 'acertos', label: 'Acertos', placeholder: '14/14' },
    ],
  },
  {
    id: 'estereopsia',
    label: 'Estereopsia (Titmus)',
    hint: 'Segundos de arco',
    category: 'outros',
    fields: [{ key: 'arco', label: 'Estereopsia', layout: 'mono', placeholder: '40" de arco' }],
  },

  // — Diagnóstico / conduta —
  // Campos clínicos (não-exame) digitáveis no atendimento. Persistem entre
  // consultas (carry-over) e alimentam os mappings `clinical.*` dos laudos
  // (ver src/services/pdf/clinicalContext.ts).
  {
    id: 'classificacao-glaucoma',
    label: 'Classificação do glaucoma',
    category: 'diagnostico',
    fields: [
      {
        key: 'classificacao',
        label: 'Classificação',
        layout: 'mono',
        kind: 'select',
        options: [
          'GPAA',
          'GPAF',
          'Glaucoma Secundário',
          'Glaucoma Congênito',
          'Suspeito de Glaucoma',
        ],
      },
    ],
  },
  {
    id: 'estadiamento-glaucoma',
    label: 'Estadiamento do glaucoma',
    category: 'diagnostico',
    fields: [
      {
        key: 'estadiamento',
        label: 'Estadiamento',
        layout: 'mono',
        kind: 'select',
        options: ['Inicial', 'Moderado', 'Avançado'],
      },
    ],
  },
  // Data do 1º atendimento com o médico que atende (28/10/2026) — registro
  // mais antigo dele no prontuário, ou hoje se ainda não há. Automática,
  // como a tonometria basal: {{ex:primeira-consulta}} no relatório.
  {
    id: 'primeira-consulta',
    label: 'Primeira consulta comigo',
    shortLabel: '1ª consulta',
    hint: 'Data do seu 1º atendimento do paciente — automática',
    category: 'diagnostico',
    fromHistory: true,
    fields: [{ key: 'data', label: 'Data', layout: 'mono', placeholder: 'DD/MM/AAAA' }],
  },
  {
    id: 'cid',
    label: 'CID-10',
    hint: 'Código diagnóstico',
    category: 'diagnostico',
    fields: [{ key: 'cid', label: 'CID-10', layout: 'mono', placeholder: 'Ex.: H40.1' }],
  },
  {
    id: 'diagnostico',
    label: 'Diagnóstico',
    category: 'diagnostico',
    // Metade da linha do cockpit: Diagnóstico e Conduta ficam lado a lado
    // (12 + 12 de 24) e cada um continua largo o bastante pra escrever.
    span: 12,
    fields: [
      {
        key: 'diagnostico',
        label: 'Diagnóstico',
        layout: 'mono',
        kind: 'textarea',
        placeholder: 'Hipóteses / diagnóstico do atendimento',
        presets: [
          'Exame oftalmológico normal',
          'Ametropia',
          'Presbiopia',
          'Catarata senil',
          'Glaucoma primário de ângulo aberto',
          'Suspeito de glaucoma',
          'Olho seco',
          'Conjuntivite alérgica',
          'Pterígio',
          'Retinopatia diabética',
        ],
      },
    ],
  },
  // Conduta do atendimento (perVisit: não faz carry-over — cada consulta tem a
  // sua). Complementa o Documento de Texto "Conduta" (que segue com o fluxo
  // próprio de retorno → portal); na evolução gerada os dois se somam na
  // mesma seção "## Conduta" (ver composeEvolucao).
  {
    id: 'conduta',
    label: 'Conduta',
    category: 'diagnostico',
    perVisit: true,
    span: 12,
    fields: [
      {
        key: 'conduta',
        label: 'Conduta',
        layout: 'mono',
        kind: 'textarea',
        placeholder: 'Ex.: colírio prescrito, campo visual, retorno em 6 meses',
        presets: [
          'Prescrição de óculos',
          'Prescrito colírio lubrificante',
          'Solicitados exames complementares',
          'Indicada cirurgia de catarata',
          'Orientações e sinais de alarme explicados',
          'Retorno em 6 meses',
          'Retorno em 1 ano',
          'Alta oftalmológica',
        ],
      },
    ],
  },
];

/** Lookup rápido por id. */
export const EXAM_MODULE_BY_ID: Record<string, ExamModuleDef> = Object.fromEntries(
  BUILTIN_EXAM_MODULES.map((m) => [m.id, m]),
);

/** Labels amigáveis por categoria pra agrupar a lista lateral. */
export const CATEGORY_LABELS: Record<ExamModuleDef['category'], string> = {
  anamnese: 'Anamnese',
  refracao: 'Refração',
  acuidade: 'Acuidade visual',
  pressao: 'Pressão / espessura',
  segmento_anterior: 'Segmento anterior',
  segmento_posterior: 'Segmento posterior',
  biometria: 'Biometria / topografia',
  diagnostico: 'Diagnóstico / conduta',
  outros: 'Outros',
};

// ─── Tipos de atendimento (presets) ───

export const BUILTIN_ENCOUNTER_TYPES: EncounterTypeDef[] = [
  {
    id: 'rotina',
    label: 'Consulta de rotina',
    description: 'AV, refração, biomicroscopia, PIO e fundoscopia',
    builtin: true,
    moduleIds: [
      'lensometria',
      'acuidade-visual',
      'refracao',
      'biomicroscopia',
      'pio',
      'fundoscopia',
    ],
  },
  {
    id: 'pre-op-catarata',
    label: 'Pré-operatório de catarata',
    description: 'AV, refração, biometria, ceratometria, biomicroscopia',
    builtin: true,
    moduleIds: [
      'acuidade-visual',
      'refracao',
      'ceratometria',
      'biometria',
      'biomicroscopia',
      'pio',
      'fundoscopia',
    ],
  },
  {
    id: 'glaucoma',
    label: 'Acompanhamento de glaucoma',
    description: 'PIO, paquimetria, escavação, campo visual, classificação e CID',
    builtin: true,
    moduleIds: [
      'acuidade-visual',
      'pio',
      'paquimetria',
      'biomicroscopia',
      'fundoscopia',
      'relacao-escavacao-disco',
      'campo-visual',
      'classificacao-glaucoma',
      'estadiamento-glaucoma',
      'cid',
      'diagnostico',
    ],
  },
  {
    id: 'pediatrica',
    label: 'Consulta pediátrica',
    description: 'AV, refração sob cicloplegia, motilidade',
    builtin: true,
    moduleIds: [
      'acuidade-visual',
      'refracao',
      'motilidade',
      'biomicroscopia',
      'fundoscopia',
    ],
  },
  {
    id: 'pre-op-refrativa',
    label: 'Pré-op refrativa (LASIK/PRK)',
    description: 'Refração, topografia, paquimetria, pupilometria',
    builtin: true,
    moduleIds: [
      'acuidade-visual',
      'refracao',
      'ceratometria',
      'topografia',
      'paquimetria',
      'biomicroscopia',
      'pio',
      'fundoscopia',
    ],
  },
];

export const ENCOUNTER_TYPE_BY_ID: Record<string, EncounterTypeDef> = Object.fromEntries(
  BUILTIN_ENCOUNTER_TYPES.map((t) => [t.id, t]),
);
